<script lang="ts">
  import type { MatchHistoryEntry, MatchOutcome } from '@2k48/shared';
  import TopBar from '../components/TopBar.svelte';
  import PixelAvatar from '../components/PixelAvatar.svelte';
  import { i18n } from '../i18n/index.svelte';
  import { session } from '../stores/session.svelte';
  import { router } from '../stores/router.svelte';
  import { api } from '../../net/client';
  import { groupNumber, shortDate, signed } from '../format';

  /**
   * The "últimas partidas" feed.
   *
   * One list for every mode: `GET /api/history/:playerId` answers with the
   * opponent already joined in, and a solo run simply comes back without one.
   */

  interface HistoryResponse {
    entries: MatchHistoryEntry[];
    total: number;
  }

  const t = $derived(i18n.t);
  const outcomeLabel = $derived<Record<MatchOutcome, string>>(t.history.outcome);
  // Modes the catalogue does not name fall back to their id, so a mode shipped
  // by the server ahead of the client still reads as something.
  const modeLabel = $derived<Record<string, string>>(t.history.mode);

  let entries = $state<MatchHistoryEntry[]>([]);
  let loading = $state(true);
  let failed = $state(false);
  const playerId = $derived(router.current.params.player || session.profile?.id);

  $effect(() => {
    const player = playerId;
    if (!player) {
      loading = false;
      return;
    }

    let cancelled = false;
    loading = true;

    void api<HistoryResponse>(`/history/${player}?limit=40`)
      .then((response) => {
        if (cancelled) return;
        entries = response.entries;
        failed = false;
      })
      .catch(() => {
        if (cancelled) return;
        entries = [];
        failed = true;
      })
      .finally(() => {
        if (!cancelled) loading = false;
      });

    return () => {
      cancelled = true;
    };
  });

  function outcomeClass(outcome: MatchOutcome): string {
    if (outcome === 'win') return 'history__outcome--win';
    if (outcome === 'draw') return 'history__outcome--draw';
    return 'history__outcome--loss';
  }
</script>

<div class="view history">
  <TopBar title={t.history.title} />

  <div class="view__body">
    {#if loading}
      <p class="view__placeholder">{t.common.loading}</p>
    {:else if failed}
      <p class="view__placeholder">{t.history.offlineMessage}</p>
    {:else if entries.length === 0}
      <p class="view__placeholder">{t.history.emptyMessage}</p>
    {:else}
      <div class="rows history__list">
        {#each entries as entry (entry.matchId)}
          <button
            type="button"
            class="row history__row profile-entry"
            disabled={!entry.opponent}
            onclick={() => entry.opponent && router.go('profile', { player: entry.opponent.id })}
          >
            <PixelAvatar avatarId={entry.opponent?.avatarId ?? 0} size="sm" />

            <span class="history__copy">
              <span class="history__top">
                <strong class="{outcomeClass(entry.outcome)} history__outcome">
                  {outcomeLabel[entry.outcome]}
                </strong>
                <span class="tiny">{modeLabel[entry.modeId] ?? entry.modeId.toUpperCase()}</span>
              </span>
              <span class="history__vs tiny">
                {entry.opponent
                  ? t.common.versusName(entry.opponent.displayName)
                  : t.history.playedSolo}
              </span>
            </span>

            <span class="history__numbers">
              <span class="row__value">{groupNumber(entry.score)}</span>
              {#if entry.ratingDelta !== null}
                <span
                  class="history__delta {entry.ratingDelta >= 0
                    ? 'history__outcome--win'
                    : 'history__outcome--loss'}"
                >
                  {signed(entry.ratingDelta)} ELO
                </span>
              {:else}
                <span class="tiny">{shortDate(entry.endedAt)}</span>
              {/if}
            </span>
          </button>
        {/each}
      </div>
    {/if}
  </div>
</div>
